<template>
  <div>
    <CRow>
      <CCol lg="12">
        <div class="d-flex align-items-center mb-4">
          <CButton
              variant="ghost"
              color="secondary"
              @click="onBack"
              class="mr-3"
          >
            <CIcon name="cipChevronLeft"/>
          </CButton>
          <div>
            <h1 class="main-header mb-0">
              <span v-if="isEditMode">{{ $pgettext('tag_definitions', 'Edit Tag Definition') }}</span>
              <span v-else>{{ $pgettext('tag_definitions', 'Create Tag Definition') }}</span>
            </h1>
            <p class="text-muted mb-0" v-if="isEditMode && currentTag">
              {{ currentTag.name }} ({{ currentTag.code }})
            </p>
          </div>
        </div>

        <PComponentLoader :loading="loading">
          <TagDefinitionForm
              v-if="!loading && isEditMode"
              :data="currentTag"
              @save="onSave"
          />
          <TagDefinitionForm
              v-else-if="!loading && !isEditMode"
              :data="emptyTag"
              @save="onSave"
          />
        </PComponentLoader>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import TagDefinitionForm from "@/domain/tagDefinitions/components/TagDefinitionForm.vue";
import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";

export default {
  name: 'TagDefinitionsEdit',
  components: {PComponentLoader, TagDefinitionForm},
  data() {
    return {
      emptyTag: {}
    }
  },
  async mounted() {
    await this.fetchConfig({type: 'tag_definition', id: this.picaServiceId})

    if (this.isEditMode) {
      await this.fetchTagDefinition({
        picaServiceId: this.picaServiceId,
        id: this.tagId
      })

      // Redirect if trying to edit an implicit tag
      if (this.currentTag && this.currentTag.is_implicit) {
        this.notifyError(
          this.$pgettext('tag_definitions.error', 'Implicit tags cannot be edited')
        )
        this.onBack()
      }

      // Redirect if trying to edit a contract-level tag without permission
      if (this.currentTag && this.currentTag.is_contract_level && !this.hasPerm('tag_definition.manage_contract_tags')) {
        this.notifyError(
          this.$pgettext('tag_definitions.error', 'Contract-level tags cannot be edited without proper permissions')
        )
        this.onBack()
      }
    } else {
      this.clearCurrentTag()
    }
  },
  computed: {
    ...mapGetters('tagDefinitions', [
      'currentTag',
      'loading',
    ]),
    picaServiceId() {
      return this.$route.params.picaServiceId
    },
    tagId() {
      return this.$route.params.id
    },
    isEditMode() {
      return !!this.tagId
    }
  },
  methods: {
    ...mapActions('tagDefinitions', [
      'fetchTagDefinition',
      'createTagDefinition',
      'updateTagDefinition',
      'clearCurrentTag'
    ]),
    ...mapActions('login', [
      'fetchConfig',
    ]),
    async onSave(formData) {
      if (!this.validate(formData)) {
        return
      }

      try {
        if (this.isEditMode) {
          await this.updateTagDefinition({
            picaServiceId: this.picaServiceId,
            id: this.tagId,
            data: formData
          })
          this.notifySuccess(
              this.$pgettext('tag_definitions.success', 'Tag definition updated successfully')
          )
        } else {
          await this.createTagDefinition({
            picaServiceId: this.picaServiceId,
            data: formData
          })
          this.notifySuccess(
              this.$pgettext('tag_definitions.success', 'Tag definition created successfully')
          )
        }
        this.onBack()
      } catch (error) {
        console.error('Error saving tag definition:', error)

        // Prova a estrarre messaggi di errore specifici dall'API
        if (error.response && error.response.data) {
          const errors = error.response.data

          // Gestisci errori sui singoli campi
          if (errors.code && Array.isArray(errors.code)) {
            this.notifyError(errors.code[0])
            return
          }

          if (errors.non_field_errors && Array.isArray(errors.non_field_errors)) {
            this.notifyError(errors.non_field_errors[0])
            return
          }
        }

        // Messaggio di errore generico
        this.notifyError(
            this.$pgettext('tag_definitions.error', 'Failed to save tag definition')
        )
      }
    },
    validate(formData) {
      if (!formData.name || formData.name.trim().length === 0) {
        this.notifyError(this.$pgettext('tag_definitions.validation', 'Name is required'))
        return false
      }

      if (!formData.code || formData.code.trim().length === 0) {
        this.notifyError(this.$pgettext('tag_definitions.validation', 'Code is required'))
        return false
      }

      const isCodeValid = /^[a-z][a-z0-9_]*$/.test(formData.code)
      if (!isCodeValid) {
        this.notifyError(this.$pgettext('tag_definitions.validation', 'Code format is invalid'))
        return false
      }

      // Validate at least one scope is selected
      const hasAtLeastOneScope = formData.allow_checkin || formData.allow_media ||
          formData.allow_face || formData.allow_person ||
          formData.allow_cluster
      if (!hasAtLeastOneScope) {
        this.notifyError(this.$pgettext('tag_definitions.validation', 'At least one scope must be selected'))
        return false
      }

      if (formData.input_config.type === 'select' || formData.input_config.type === 'checkbox') {
        if (!formData.input_config.options || formData.input_config.options.length === 0) {
          this.notifyError(this.$pgettext('tag_definitions.validation', 'At least one option is required'))
          return false
        }

        const hasEmptyOptions = formData.input_config.options.some(opt => !opt.value || !opt.label)
        if (hasEmptyOptions) {
          this.notifyError(this.$pgettext('tag_definitions.validation', 'All options must have value and label'))
          return false
        }

        const values = formData.input_config.options.map(opt => opt.value)
        const uniqueValues = new Set(values)
        if (values.length !== uniqueValues.size) {
          this.notifyError(this.$pgettext('tag_definitions.validation', 'Option values must be unique'))
          return false
        }
      }

      return true
    },
    onBack() {
      this.$router.push({
        name: 'tagDefinitions.list',
        params: {picaServiceId: this.picaServiceId}
      })
    }
  },
  beforeDestroy() {
    this.clearCurrentTag()
  }
}
</script>

<style scoped>
.main-header {
  font-size: 1.75rem;
}
</style>
